import { useState } from 'react';
import type { CreativeBriefInput, CreativeChannel } from '../../shared/types';
import { CREATIVE_CHANNEL_OPTIONS } from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';
import TypologyShape from './ui/TypologyShape';
import BackButton from './ui/BackButton';

interface Props {
  error: string | null;
  onSubmit: (data: { textContent: string }) => void;
  onBack: () => void;
}

export default function CreativeBriefForm({ error, onSubmit, onBack }: Props) {
  const [brand, setBrand] = useState('');
  const [product, setProduct] = useState('');
  const [positioning, setPositioning] = useState('');
  const [target, setTarget] = useState('');
  const [channel, setChannel] = useState<CreativeChannel>('social');
  const [message, setMessage] = useState('');
  const [constraints, setConstraints] = useState('');

  const canSubmit = brand.trim() && product.trim() && target.trim() && message.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const brief: CreativeBriefInput = {
      brand: brand.trim(),
      product: product.trim(),
      positioning: positioning.trim() || undefined,
      target: target.trim(),
      channel,
      message: message.trim(),
      constraints: constraints.trim() || undefined,
    };
    // Send as JSON string in textContent — backend parses it
    onSubmit({ textContent: JSON.stringify(brief) });
  };

  return (
    <div className="animate-app-in max-w-2xl mx-auto">
      {/* Retour + bandeau d'en-tête teinté par la typologie */}
      <BackButton onClick={onBack} label="Retour" />
      <div className="hero-tint mb-6 flex items-center gap-4 rounded-3xl px-5 py-5 sm:px-6">
        <span className="hero-pill grid h-12 w-12 shrink-0 place-items-center">
          <TypologyShape type="creative_brief" color="currentColor" />
          <AnalysisIcon type="creative_brief" className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[1.6rem] leading-tight text-ink sm:text-2xl">
            Visaly Cr&eacute;a
          </h2>
          <p className="text-sm text-ink/65">
            D&eacute;crivez votre brief cr&eacute;atif pour recevoir des id&eacute;es marketing compatibles avec le r&eacute;f&eacute;rentiel ANSM
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Row 1: Brand + Product */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Marque *
            </label>
            <div className="glow-input">
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder='Ex: "Laboratoires Alvea"'
                className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-2.5 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Produit *
            </label>
            <div className="glow-input">
              <input
                type="text"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder='Ex: "Compl&eacute;ment alimentaire sommeil"'
                className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-2.5 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow"
              />
            </div>
          </div>
        </div>

        {/* Positioning (optional) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Positionnement
            <span className="text-slate-400 font-normal ml-1">(optionnel)</span>
          </label>
          <div className="glow-input">
            <input
              type="text"
              value={positioning}
              onChange={(e) => setPositioning(e.target.value)}
              placeholder='Ex: "Premium, formules d&#39;origine v&eacute;g&eacute;tale, fabriqu&eacute; en France"'
              className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-2.5 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow"
            />
          </div>
        </div>

        {/* Target audience */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Cible *
          </label>
          <div className="glow-input">
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder='Ex: "Adultes 30-55 ans, attentifs &agrave; leur sommeil et &agrave; leur immunit&eacute;"'
              className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-2.5 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow"
            />
          </div>
        </div>

        {/* Channel */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Canal de diffusion
          </label>
          <div className="flex flex-wrap gap-2">
            {CREATIVE_CHANNEL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setChannel(opt.value)}
                className={`chip-tint px-3.5 py-1.5 rounded-full text-sm ${channel === opt.value ? 'is-on' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Message &agrave; faire passer *
          </label>
          <div className="glow-input">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder='Ex: "Mettre en avant le savoir-faire du laboratoire et la qualit&eacute; des formules pour la saison hivernale"'
              rows={3}
              className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow resize-y min-h-[3rem]"
            />
          </div>
        </div>

        {/* Constraints (optional) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Contraintes particuli&egrave;res
            <span className="text-slate-400 font-normal ml-1">(optionnel)</span>
          </label>
          <div className="glow-input">
            <textarea
              value={constraints}
              onChange={(e) => setConstraints(e.target.value)}
              placeholder='Ex: "Budget limit&eacute;, pas de vid&eacute;o, doit inclure les mentions obligatoires, diffusion en officine uniquement"'
              rows={2}
              className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow resize-y min-h-[2.5rem]"
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="btn-ink px-6 py-3 text-sm"
        >
          G&eacute;n&eacute;rer 6 id&eacute;es cr&eacute;atives
        </button>
      </div>

      {/* Info box */}
      <div className="card-tint mt-6 p-4 rounded-2xl">
        <p className="text-sm text-[var(--tint-deep)]">
          <strong>Visaly Cr&eacute;a</strong> g&eacute;n&egrave;re 6 territoires cr&eacute;atifs distincts, chacun valid&eacute; par notre moteur juridique.
          Chaque id&eacute;e inclut un concept, des slogans pr&ecirc;ts &agrave; l&apos;emploi, une direction artistique et les articles de loi qui l&apos;autorisent.
        </p>
      </div>
    </div>
  );
}
